import { useMemo } from 'react';

import type { PanelMention } from '../lib/panel-mentions';
import { MentionMenu, type MentionMenuItem } from './MentionMenu';

interface PanelMentionMenuProps {
  /** Candidates for the current query. The suggestion plugin has already filtered them. */
  mentions: PanelMention[];
  onSelect: (mention: PanelMention) => void;
  onClose: () => void;
  /** DOM id for the listbox, so the composer can name it in `aria-controls`. */
  id: string;
  /** Reports the active option's DOM id, which the composer carries as `aria-activedescendant`. */
  onActiveOptionChange?: (optionId: string | undefined) => void;
}

/**
 * Autocomplete menu shown when the user types "@" in the chat composer.
 *
 * Lists the dashboard's panels by their mention label, which carries an ordinal when two panels share
 * a title. Choosing one writes a node into the document, so the label is what the reader sees and the
 * panel id is what the message reports.
 *
 * It shows no notice. A message may name as many panels as it likes, and the composer hides the menu
 * when nothing matches — so there is no state left in which the menu has something to say and no rows.
 */
export function PanelMentionMenu({
  mentions,
  onSelect,
  onClose,
  id,
  onActiveOptionChange,
}: PanelMentionMenuProps): React.ReactElement {
  const items: MentionMenuItem[] = useMemo(
    () => mentions.map((mention) => ({ id: mention.id, label: mention.label })),
    [mentions],
  );

  return (
    <MentionMenu
      items={items}
      onSelect={(item) => {
        const picked = mentions.find((mention) => mention.id === item.id);
        if (picked) onSelect(picked);
      }}
      onClose={onClose}
      label="Panels"
      id={id}
      onActiveOptionChange={onActiveOptionChange}
      testId="panel-mention-menu"
      optionPrefix="panel-option"
    />
  );
}
